import { Chip, colors } from '@mui/material';
import { styled } from '@mui/material/styles';

export const HeaderLeft = styled('div')({
  display: 'flex',
  alignItems: 'baseline',
  gap: 8,
  flexWrap: 'wrap',
});

export const AvgPrice = styled('span')(({ theme }) => ({
  fontSize: '1.4rem',
  fontWeight: 700,
  color: theme.palette.text.primary,
  lineHeight: 1,
}));

export const ChangeLabel = styled('span', {
  shouldForwardProp: prop => prop !== 'positive',
})<{ positive: boolean }>(({ positive }) => ({
  fontSize: '0.82rem',
  fontWeight: 600,
  color: positive ? colors.green[700] : colors.red[700],
}));

export const ViewToggle = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  '& .MuiToggleButton-root': {
    fontSize: '0.72rem',
    padding: '2px 12px',
    textTransform: 'none',
    fontWeight: 600,
  },
  '& .MuiToggleButton-root.Mui-selected': {
    backgroundColor: '#1976d2',
    color: '#fff',
    '&:hover': { backgroundColor: '#1565c0' },
  },
});

export const FundFilterRow = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(0.75),
  flexWrap: 'wrap',
  marginBottom: theme.spacing(1.5),
}));

export const ControlsRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(1.5),
  '& .MuiToggleButton-root': {
    fontSize: '0.72rem',
    padding: '2px 10px',
    textTransform: 'none',
    fontWeight: 600,
  },
  '& .MuiToggleButton-root.Mui-selected': {
    backgroundColor: '#1976d2',
    color: '#fff',
    '&:hover': { backgroundColor: '#1565c0' },
  },
}));

export const FundPill = styled(Chip, {
  shouldForwardProp: prop => prop !== 'active',
})<{ active?: boolean }>(({ active }) => ({
  fontSize: '0.72rem',
  fontWeight: 600,
  cursor: 'pointer',
  backgroundColor: active ? '#1976d2' : 'transparent',
  borderColor: '#1976d2',
  color: active ? '#fff' : '#1976d2',
  '& .MuiChip-label': { paddingLeft: 10, paddingRight: 10 },
  '&:hover': {
    backgroundColor: active ? '#1976d2' : '#1976d222',
  },
}));

export const MarksTable = styled('table')(({ theme }) => ({
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.8rem',
  marginTop: theme.spacing(1),
}));

export const MarksTableRow = styled('tr')(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.grey[200]}`,
  '&:last-child': { borderBottom: 'none' },
}));

export const MarksTableHead = styled('th')<{ align?: 'left' | 'right' | 'center' }>(({ theme, align = 'left' }) => ({
  padding: `${theme.spacing(0.75)} ${theme.spacing(1)}`,
  textAlign: align,
  fontWeight: 700,
  fontSize: '0.7rem',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: theme.palette.text.secondary,
  borderBottom: `2px solid ${theme.palette.grey[200]}`,
}));

export const MarksTableCell = styled('td')<{ align?: 'left' | 'right' | 'center' }>(({ theme, align = 'left' }) => ({
  padding: `${theme.spacing(0.75)} ${theme.spacing(1)}`,
  textAlign: align,
  color: theme.palette.text.primary,
  verticalAlign: 'middle',
}));

export const TabRow = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(1.5),
  borderBottom: `1px solid ${theme.palette.grey[200]}`,
  paddingBottom: theme.spacing(0.5),
}));

interface TabButtonProps {
  active?: boolean;
}

export const TabButton = styled('button', {
  shouldForwardProp: prop => prop !== 'active',
})<TabButtonProps>(({ theme, active }) => ({
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: `${theme.spacing(0.5)} ${theme.spacing(1)}`,
  fontSize: '0.8rem',
  fontWeight: active ? 700 : 400,
  color: active ? theme.palette.primary.main : theme.palette.text.secondary,
  borderBottom: active ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
  marginBottom: '-1px',
  transition: 'color 0.15s, border-color 0.15s',
  '&:hover': {
    color: theme.palette.primary.main,
  },
}));

export const CardHeaderRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(1.5),
}));
